import {
  Box,
  Button,
  Flex,
  Icon,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react'
import React, { ReactNode } from 'react'
import { MdPhoneInTalk } from 'react-icons/md'
import useFormatMessage from '../../../hooks/useFormatMessage'
import { p2r } from '../../../utils/font'

/**
 * リスト
 */
export const VideoGroupList: React.FC<{ children: ReactNode }> = ({
  children,
}) => <Flex direction="column" gap="16px">{children}</Flex>

/**
 * メインコンポーネント
 */
export type VideoGroupListItemProps = {
  title: string // グループ名
  status: 'active' | 'reserved' // 状態（使用中/予約）
  children?: ReactNode // 可変部分
  buttons?: ReactNode // ボタン部分
  onClick?: () => void
  withBorder?: boolean // 枠線の有無
  disabled?: boolean // ボタンのdisabled状態
}

export const VideoGroupListItem: React.FC<VideoGroupListItemProps> = (
  props,
) => {
  const { f } = useFormatMessage()
  const isMobile = useBreakpointValue({ base: true, md: false })

  // ステータス
  const isActive = props.status === 'active'
  // 枠線の有無（デフォルトはtrue）
  const withBorder = props.withBorder !== undefined ? props.withBorder : true
  
  // レスポンシブなボーダー幅の設定
  let borderWidthValue = "0"
  let borderBottomWidthValue = "1px"
  let lastBorderBottomWidthValue: string | number = 0
  
  if (withBorder) {
    if (isMobile) {
      borderWidthValue = "2px"
      borderBottomWidthValue = "2px"
      lastBorderBottomWidthValue = "2px"
    } else {
      // タブレット・PCでは全体ボーダーのみ使用（下ボーダーは重複を避けるため無し）
      borderWidthValue = "1px"
      borderBottomWidthValue = "0"
      lastBorderBottomWidthValue = 0
    }
  }

  return (
    <Flex
      px={isMobile ? '16px' : '39px'}
      py={isMobile ? '16px' : '21px'}
      justify="space-between"
      align="center"
      backgroundColor="grand.white"
      columnGap={isMobile ? "8px" : "35px"}
      borderWidth={borderWidthValue}
      borderColor="#A8A8A8"
      borderRadius="10px"
      borderBottomWidth={borderBottomWidthValue}
      borderBottomColor={withBorder ? "#A8A8A8" : "#eee"}
      _last={{ borderBottomWidth: lastBorderBottomWidthValue }}
      onClick={() => {
        if (props.onClick) {
          props.onClick()
        }
      }}
      width="100%"
      maxWidth="100%"
      margin={isMobile ? "0" : "0 auto"}
    >
      <Box
        display="flex"
        flexDirection="column"
        rowGap="4px"
        columnGap="24px"
        flexWrap="wrap"
        flex="1"
        overflow="hidden"
      >
        <Box
          display="flex"
          gap={isMobile ? '0px' : '19px'}
          alignItems={isMobile ? 'flex-start' : 'center'}
          w="fit-content"
          flexDirection={isMobile ? 'column' : 'row'}
        >
          <Text
            backgroundColor={isActive ? '#C84545' : '#6AA43B'}
            color="text.white"
            fontSize={isMobile ? p2r(12) : p2r(16)}
            fontWeight="700"
            borderRadius="30px"
            px="9px"
            py="2px"
            height="fit-content"
            minW={isMobile ? p2r(64) : p2r(66)}
            textAlign="center"
            fontFamily="'BIZ UDPGothic', sans-serif"
          >
            {isActive ? f('使用中') : f('予約')}
          </Text>
          <Text
            color="#494C4F"
            fontSize={p2r(24)}
            fontWeight="700"
            lineHeight="150%"
            flex="1"
            fontFamily="'BIZ UDPGothic', sans-serif"
          >
            {props.title}
          </Text>
        </Box>

        {/* 可変部分 */}
        <Box minH={p2r(24)} maxW="100%">
          {props.children}
        </Box>
      </Box>

      {/* ボタン部分 */}
      <Box flexShrink={0}>
        {props.buttons || (
          <Button
            px={isMobile ? '16px' : '8px'}
            py="12px"
            variant="outline"
            borderRadius="80px"
            backgroundColor="#6AA43B"
            borderWidth="0"
            display="flex"
            columnGap="10px"
            w={isMobile ? 'fit-content' : p2r(148)}
            h={isMobile ? p2r(40) : p2r(48)}
            disabled={props.disabled}
          >
            <Icon
              as={MdPhoneInTalk}
              boxSize={isMobile ? p2r(20) : p2r(24)}
              color="text.white"
            />
            <Text
              fontSize={p2r(16)}
              color="#FFFDFC"
              fontWeight="700"
              fontFamily="'BIZ UDPGothic', sans-serif"
            >
              {f('入室する')}
            </Text>
          </Button>
        )}
      </Box>
    </Flex>
  )
}
